/**
 * The window's Workflow Builder (core WorkflowBuilderViewModel), mounted once by the shell. The sidebar's
 * "Workflows" row and a channel's workflows menu open it with `openWorkflows()`.
 */
import { WorkflowBuilderViewModel } from "@bundle/core";
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useOwned } from "../../hooks/owned.ts";
import { useViewModel, useWorkspace } from "../../hooks/index.ts";
import { WorkflowBuilderSheet } from "./WorkflowBuilder.tsx";

type OpenRequest = { workflowId?: string; newInChannel?: string };

let pending: OpenRequest = {};
let seq = 0;
const listeners = new Set<() => void>();

/** Opens the builder: the list, one workflow, or a new workflow in a channel. */
export function openWorkflows(request: OpenRequest = {}): void {
  pending = request;
  seq += 1;
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};

/** The workspace can use workflows (the client has the API). */
export function useWorkflowsSupported(): boolean {
  const ws = useWorkspace();
  return ws.vm.deps.api.listWorkflows !== undefined;
}

export function WorkflowsHost() {
  const ws = useWorkspace();
  const create = useCallback(() => new WorkflowBuilderViewModel(ws.vm, () => Intl.DateTimeFormat().resolvedOptions().timeZone), [ws.vm]);
  const vm = useOwned(`workflows:${ws.vm.wsId}`, create);
  const state = useViewModel(vm);
  const request = useSyncExternalStore(subscribe, () => seq);
  useEffect(() => {
    if (request > 0) vm.open(pending);
  }, [request, vm]);
  if (!state.supported) return null;
  return <WorkflowBuilderSheet vm={vm} state={state} />;
}
